import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  Search,
  FileText,
  Euro,
  Calendar,
  Building2,
  FolderOpen,
  CheckCircle,
  Clock,
  XCircle,
  Send,
  Filter,
} from 'lucide-react';
import { BudgetFormDialog } from '@/components/Budgets/BudgetFormDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface Budget {
  id: string;
  budget_number: number;
  title: string;
  customer_id: string;
  project_id: string;
  status: string;
  source: string;
  total_amount: number;
  issue_date: string;
  valid_until: string;
  customer?: { company_name: string; codificacion: string };
  project?: { name: string; project_number: number };
}

export default function Budgets() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);

  const fetchBudgets = async () => {
    try {
      const { data, error } = await api
        .from('budgets')
        .select('*')
        .eq('is_active', true)
        .order('budget_number', { ascending: false });

      if (error) throw error;

      // Fetch related data
      const customerIds = [...new Set((data || []).map((b: any) => b.customer_id).filter(Boolean))];
      const projectIds = [...new Set((data || []).map((b: any) => b.project_id).filter(Boolean))];

      const [customersRes, projectsRes] = await Promise.all([
        customerIds.length > 0
          ? api.from('customers').select('id,company_name,codificacion').in('id', customerIds)
          : { data: [] },
        projectIds.length > 0
          ? api.from('projects').select('id,name,project_number').in('id', projectIds)
          : { data: [] },
      ]);

      const customersMap = Object.fromEntries((customersRes.data || []).map((c: any) => [c.id, c]));
      const projectsMap = Object.fromEntries((projectsRes.data || []).map((p: any) => [p.id, p]));

      const enrichedData = (data || []).map((b: any) => ({
        ...b,
        customer: customersMap[b.customer_id],
        project: projectsMap[b.project_id],
      }));

      setBudgets(enrichedData);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los presupuestos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  const filteredBudgets = budgets.filter(budget => {
    const matchesSearch =
      budget.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      budget.customer?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      budget.project?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(budget.budget_number).includes(searchTerm);

    const matchesStatus = statusFilter === 'all' || budget.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleEdit = (budget: Budget) => {
    setSelectedBudget(budget);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedBudget(null);
    setDialogOpen(true);
  };

  const getStatusConfig = (status: string) => {
    const config: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: any }> = {
      draft: { label: 'Borrador', variant: 'secondary', icon: FileText },
      sent: { label: 'Enviado', variant: 'default', icon: Send },
      approved: { label: 'Aprobado', variant: 'outline', icon: CheckCircle },
      rejected: { label: 'Rechazado', variant: 'destructive', icon: XCircle },
      expired: { label: 'Expirado', variant: 'secondary', icon: Clock },
    };
    return config[status] || config.draft;
  };

  const getSourceLabel = (source: string) => {
    const labels: Record<string, string> = {
      perseo: 'Perseo',
      manual: 'Manual',
      import: 'Importado',
    };
    return labels[source] || source;
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const stats = {
    total: budgets.length,
    draft: budgets.filter(b => b.status === 'draft').length,
    sent: budgets.filter(b => b.status === 'sent').length,
    approved: budgets.filter(b => b.status === 'approved').length,
    totalAmount: budgets.filter(b => b.status === 'approved').reduce((sum, b) => sum + (b.total_amount || 0), 0),
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Presupuestos
            </h1>
            <p className="text-muted-foreground mt-2">
              Gestión de presupuestos y cotizaciones
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Presupuesto
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {stats.total}
                </p>
              </div>
              <FileText className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Borradores</p>
                <p className="text-3xl font-bold text-amber-500">{stats.draft}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Aprobados</p>
                <p className="text-3xl font-bold text-green-500">{stats.approved}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Aprobado</p>
                <p className="text-2xl font-bold text-primary">
                  {stats.totalAmount.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </p>
              </div>
              <Euro className="w-8 h-8 text-primary/50" />
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar presupuestos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="sent">Enviado</SelectItem>
              <SelectItem value="approved">Aprobado</SelectItem>
              <SelectItem value="rejected">Rechazado</SelectItem>
              <SelectItem value="expired">Expirado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Budgets Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredBudgets.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm || statusFilter !== 'all' ? 'No se encontraron presupuestos' : 'No hay presupuestos registrados'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBudgets.map((budget) => {
              const statusConfig = getStatusConfig(budget.status);
              const StatusIcon = statusConfig.icon;

              return (
                <Card key={budget.id} className="glass-card p-6 hover:shadow-magma transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="font-mono bg-primary/10">
                          PRE-{String(budget.budget_number).padStart(4, '0')}
                        </Badge>
                        <Badge variant={statusConfig.variant} className="gap-1">
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-lg line-clamp-1">{budget.title}</h3>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {budget.customer && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Building2 className="w-4 h-4" />
                        <span className="truncate">{budget.customer.company_name}</span>
                      </div>
                    )}

                    {budget.project && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <FolderOpen className="w-4 h-4" />
                        <span className="truncate">
                          P-{String(budget.project.project_number).padStart(4, '0')} - {budget.project.name}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {budget.issue_date ? format(new Date(budget.issue_date), 'dd MMM yyyy', { locale: es }) : '-'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Euro className="w-4 h-4 text-primary" />
                      <span className="font-semibold text-lg text-primary">
                        {(budget.total_amount || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border/50 flex gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {getSourceLabel(budget.source)}
                    </Badge>
                    {canManage && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto"
                        onClick={() => handleEdit(budget)}
                      >
                        Editar
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <BudgetFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        budget={selectedBudget}
        onSuccess={fetchBudgets}
      />
    </DashboardLayout>
  );
}
